import { useContext, useEffect, useState } from 'react';
import { AddModelPortfolioSecuritiesVisual } from './addModelPortfolioSecurities.visual';
import { WorkflowContext } from '../../workflowCompletion';
import { Security } from '../../../../3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { getBackendLanguage } from '../../../../../assets/i18n/config';
import { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio';
import { useSaveModelPortfolio } from '../utils/useSaveModelPortfolio';

export const AddModelPortfolioSecurities = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const { workflowData, setWorkflowData, updateWorkflowContext } = useContext(WorkflowContext);
  const [modelPortfolioDetails, setModelPortfolioDetails] = useState<ModelPortfolioData>(workflowData.modelPortfolioDetails);
  const [securities, setSecurities] = useState<Security[]>(modelPortfolioDetails?.securities || []);
  const { saveModelPortfolio, loading } = useSaveModelPortfolio();

  const colorList = ['supportOne', 'supportTwo', 'supportThree', 'supportFour', 'supportFive', 'supportSix'];

  useEffect(() => {
    // update workflow to maintain state on back and forth of substeps.
    setWorkflowData({
      ...workflowData,
      modelPortfolioDetails: {
        ...modelPortfolioDetails,
        securities,
      },
    });
  }, [securities, modelPortfolioDetails, workflowData, setWorkflowData]);

  const handleAddEmptySecurity = () => {
    // Calculate the next tax rank by finding the highest existing rank and adding 1
    const nextTaxRank = securities.length > 0
      ? Math.max(...securities.map((security) => security.taxRank ?? 0)) + 1
      : 1;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newSecurity: Security = {
      financialProduct: {
        id: tempId,
        name: '',
        primaryAssetClass: undefined,
        translatedName: {
          en: '',
          fr: '',
        },
      },
      isCashEquivalent: false,
      taxRank: nextTaxRank,
      percentage: 0,
      id: tempId,
      color: 'supportOne',
    };
    const updatedSecurities = [...securities, newSecurity];
    setSecurities(updatedSecurities);
  };

  const handleUpdateSecurity = (index: number, security: Security) => {
    const updatedSecurities = [...securities];
    const currentSecurity = updatedSecurities[index];
    const updatedSecurity = { ...security } as Security;

    if (currentSecurity.financialProduct.id?.startsWith('temp-') && !security.financialProduct.id) {
      updatedSecurity.financialProduct.id = currentSecurity.financialProduct.id;
      updatedSecurity.id = currentSecurity.financialProduct.id;
    }

    updatedSecurities[index] = updatedSecurity;

    const seenAssetClasses = new Set<string>();
    const orderedAssetClasses = updatedSecurities.reduce<string[]>((acc, sec) => {
      const assetClass = sec.financialProduct.primaryAssetClass?.translatedName?.[getBackendLanguage()] || sec.financialProduct.primaryAssetClass?.name;
      if (assetClass && !seenAssetClasses.has(assetClass)) {
        seenAssetClasses.add(assetClass);
        acc.push(assetClass);
      }
      return acc;
    }, []);

    const newColorMap = orderedAssetClasses.reduce<Record<string, 'supportOne' | 'supportTwo' | 'supportThree' | 'supportFour' | 'supportFive' | 'supportSix'>>((acc, assetClass, i) => ({
      ...acc,
      [assetClass]: colorList[i % colorList.length] as 'supportOne' | 'supportTwo' | 'supportThree' | 'supportFour' | 'supportFive' | 'supportSix',
    }), {});

    const finalSecurities = updatedSecurities.map((sec) => {
      const assetClass = sec.financialProduct.primaryAssetClass?.translatedName?.[getBackendLanguage()] || sec.financialProduct.primaryAssetClass?.name;
      return {
        ...sec,
        color: assetClass ? newColorMap[assetClass] : 'supportOne',
      };
    });

    setSecurities(finalSecurities);
    setModelPortfolioDetails({ ...modelPortfolioDetails, securities: finalSecurities });
  };

  const handleRemoveSecurity = (index: number) => {
    const updatedSecurities = securities.filter((_: any, i: number) => i !== index);
    setSecurities(updatedSecurities);
    setModelPortfolioDetails({ ...modelPortfolioDetails, securities: updatedSecurities });
  };

  const handleUpdateTaxRank = (index: number, value: number) => {
    const updatedSecurities = [...securities];
    updatedSecurities[index].taxRank = value;
    setSecurities(updatedSecurities);
    setModelPortfolioDetails({ ...modelPortfolioDetails, securities: updatedSecurities });
  };

  const handleUpdateTargetPercentage = (index: number, value: number) => {
    const updatedSecurities = [...securities];
    updatedSecurities[index].percentage = value;
    setSecurities(updatedSecurities);
    setModelPortfolioDetails({ ...modelPortfolioDetails, securities: updatedSecurities });
  };

  const handleImportFromCsv = () => {
    // TODO: Implement CSV import functionality
  };

  const handleReorderSecurities = (newSecurities: Security[]) => {
    const cashSecurities = newSecurities.filter((security) => security.financialProduct.isCash);
    const nonCashSecurities = newSecurities.filter((security) => !security.financialProduct.isCash);

    const updatedSecurities = [...nonCashSecurities, ...cashSecurities].map((security, index) => ({
      ...security,
      financialProduct: {
        ...security.financialProduct,
        id: security.financialProduct.id,
      },
      id: security.id || security.financialProduct.id,
      taxRank: security.financialProduct.isCash ? undefined : index + 1,
      percentage: security.percentage,
      name: security.financialProduct.name,
    }));

    setSecurities(updatedSecurities);
    setModelPortfolioDetails({ ...modelPortfolioDetails, securities: updatedSecurities });
  };

  const continueFunc = async () => {
    try {
      await setWorkflowData({
        ...workflowData,
        modelPortfolioDetails: {
          ...workflowData.modelPortfolioDetails,
          securities,
        },
      });

      if (updateWorkflowContext) {
        await updateWorkflowContext();
      }

      onNext();
    } catch (error) { /* empty */ }
  };

  const handleSaveForLater = async () => {
    try {
      const savedModelPortfolioData = await saveModelPortfolio(modelPortfolioDetails);

      const savedData = {
        ...savedModelPortfolioData,
        originalValues: savedModelPortfolioData,
      };
      setModelPortfolioDetails(savedData);
    } catch (error) {
      // Error is already handled by saveModelPortfolio
    }
  };

  return (
    <AddModelPortfolioSecuritiesVisual
      options={options}
      workflowCompletion={workflowCompletion}
      securities={securities}
      continueFunc={continueFunc}
      onAddSecurity={handleAddEmptySecurity}
      onUpdateSecurity={handleUpdateSecurity}
      onRemoveSecurity={handleRemoveSecurity}
      onUpdateTaxRank={handleUpdateTaxRank}
      onUpdateTargetPercentage={handleUpdateTargetPercentage}
      onSaveForLater={handleSaveForLater}
      onImportFromCsv={handleImportFromCsv}
      onReorderSecurities={handleReorderSecurities}
      modelPortfolioDetails={modelPortfolioDetails}
      loading={loading}
    />
  );
};

export default AddModelPortfolioSecurities;
